'use client'

import PaginationControls from '@/components/pagination/PaginationControls'
import {
    ApprovalCompany,
    ApprovedBillingApprovalItem,
    getApprovedBillingApprovals,
} from '@/lib/api/financial/billing-approval-api'
import {
    ApprovedBillingActionRequestItem,
    getApprovedBillingActionRequests,
} from '@/lib/api/financial/billing-action-request-api'
import {
    ApprovedRefundItem,
    downloadRefundReceiptPdf,
    getApprovedRefundRequests,
    getRefundReceiptUrl,
} from '@/lib/api/financial/billing-refund-api'
import { toast } from 'react-toastify'
import ReceiptPreviewModal from '@/components/billing/receipt-preview-modal'
import { useCallback, useEffect, useState } from 'react'
import {
    MdHistory,
    MdListAlt,
    MdOpenInNew,
    MdPerson,
    MdPictureAsPdf,
    MdReceipt,
    MdReplay,
    MdTask,
    MdDownload,
    MdPrint,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import CompanyBadge from './components/company-badge'
import PayoutBatchItemsModal from './components/payout-batch-items-modal'
import {
    findStatementAttachment,
    formatCurrency,
    formatDateTime,
} from './format'

const PAGE_SIZE = 10

const ACTION_LABELS: Record<string, string> = {
    MARK_PAID: 'Marcar como pago',
    CANCEL: 'Cancelar cobrança',
    REFUND: 'Estorno',
}

type HistoryKind = 'actions' | 'payables' | 'refunds'

const KIND_TABS: { key: HistoryKind; label: string; icon: typeof MdTask }[] = [
    { key: 'payables', label: 'Contas a pagar', icon: MdReceipt },
    { key: 'actions', label: 'Ações', icon: MdTask },
    { key: 'refunds', label: 'Estornos', icon: MdReplay },
]

/** Linha do histórico já normalizada — as três origens caem no mesmo card. */
interface HistoryEntry {
    id: number
    /** Lançamento por trás da linha — destino do clique e base das ações. */
    billingId: number | null
    title: string
    counterparty: string
    amount: number
    approvedBy: string
    approvedAt: string | null
    tag?: string
    /** Solicitação agrupada: habilita "Ver grupo". */
    isPayoutBatch?: boolean
    /** Demonstrativo em PDF, quando o lançamento tem um anexado. */
    statement?: { url: string; name: string } | null
    /** Filial dona do registro — o histórico é da organização inteira. */
    company?: ApprovalCompany | null
    /**
     * Estorno aprovado: gera recibo em PDF sob demanda (`id` é o da solicitação).
     * As outras origens não têm documento equivalente.
     */
    hasRefundReceipt?: boolean
}

function toEntryFromAction(r: ApprovedBillingActionRequestItem): HistoryEntry {
    const item = r as any
    return {
        id: item.id,
        billingId: item.billing?.id ?? null,
        title: item.billing?.description || `Lançamento #${item.billing?.id ?? ''}`,
        counterparty:
            item.billing?.client?.name || item.billing?.partner?.name || '—',
        amount: item.billing?.amount ?? 0,
        approvedBy: item.approvedBy?.name || '—',
        approvedAt: item.approvedAt ?? null,
        tag: ACTION_LABELS[item.action] || item.action,
        isPayoutBatch: item.billing?.isPayoutBatch ?? false,
        statement: findStatementAttachment(item.billing?.attachments),
        company: item.company ?? null,
    }
}

function toEntryFromPayable(r: ApprovedBillingApprovalItem): HistoryEntry {
    const item = r as any
    return {
        id: item.id,
        billingId: item.id,
        title: item.description || `Lançamento #${item.id}`,
        counterparty:
            item.supplier?.name ||
            item.partner?.name ||
            item.professional?.name ||
            '—',
        amount: item.amount ?? 0,
        approvedBy: item.approvedBy?.name || '—',
        approvedAt: item.approvedAt ?? null,
        isPayoutBatch: item.isPayoutBatch ?? false,
        statement: findStatementAttachment(item.attachments),
        company: item.company ?? null,
    }
}

function toEntryFromRefund(r: ApprovedRefundItem): HistoryEntry {
    const item = r as any
    return {
        id: item.id,
        billingId: item.billing?.id ?? null,
        title: item.billing?.description || `Cobrança #${item.billing?.id ?? ''}`,
        counterparty:
            item.billing?.client?.name || item.billing?.partner?.name || '—',
        amount: item.billing?.refundedAmount ?? item.billing?.amount ?? 0,
        approvedBy: item.approvedBy?.name || '—',
        approvedAt: item.approvedAt ?? null,
        tag: 'Estorno',
        company: item.company ?? null,
        hasRefundReceipt: true,
    }
}

async function fetchHistory(
    kind: HistoryKind,
    page: number,
    search?: string,
): Promise<{ entries: HistoryEntry[]; total: number } | null> {
    if (kind === 'actions') {
        const res = await getApprovedBillingActionRequests(page, PAGE_SIZE, search)
        if (!res.success || !res.result) return null
        return {
            entries: res.result.items.map(toEntryFromAction),
            total: res.result.total,
        }
    }
    if (kind === 'payables') {
        const res = await getApprovedBillingApprovals(page, PAGE_SIZE, search)
        if (!res.success || !res.result) return null
        return {
            entries: res.result.items.map(toEntryFromPayable),
            total: res.result.total,
        }
    }
    const res = await getApprovedRefundRequests(page, PAGE_SIZE, search)
    if (!res.success || !res.result) return null
    return {
        entries: res.result.items.map(toEntryFromRefund),
        total: res.result.total,
    }
}

/**
 * Histórico do que já foi decidido.
 *
 * As três origens (ações, contas a pagar, estornos) têm campos diferentes, mas a
 * pergunta que se faz aqui é sempre a mesma — "quem aprovou o quê, quando e de quanto
 * era" —, então são normalizadas em `HistoryEntry` e caem no mesmo card. Antes eram
 * três tabelas empilhadas, cada uma com paginação própria de setinhas.
 */
interface ApprovedHistorySectionProps {
    /**
     * Termo vindo da busca da tela. O histórico é paginado no banco, então o recorte
     * também é feito lá — filtrar as dez linhas da página aberta responderia "nada
     * encontrado" para algo que existe na página seguinte.
     */
    search?: string
}

export default function ApprovedHistorySection({
    search,
}: ApprovedHistorySectionProps = {}) {
    const [kind, setKind] = useState<HistoryKind>('payables')
    const [entries, setEntries] = useState<HistoryEntry[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()

    /** Composição do lote e demonstrativo abertos a partir do histórico. */
    const [batchId, setBatchId] = useState<number | null>(null)
    const [statement, setStatement] = useState<{
        url: string
        name: string
    } | null>(null)

    /**
     * Recibo do estorno aberto para visualizar/imprimir. Vem por blob (a rota pede
     * token), então a URL é revogada ao fechar — senão o objeto fica na memória da
     * aba até o F5.
     */
    const [receipt, setReceipt] = useState<{ url: string; name: string } | null>(
        null,
    )
    /** Id em processamento — o PDF é renderizado no servidor e demora. */
    const [receiptLoadingId, setReceiptLoadingId] = useState<number | null>(null)

    const openReceipt = useCallback(async (entryId: number) => {
        setReceiptLoadingId(entryId)
        try {
            const url = await getRefundReceiptUrl(entryId)
            setReceipt({ url, name: `Recibo de estorno #${entryId}` })
        } catch {
            toast.error('Não foi possível gerar o recibo deste estorno.')
        } finally {
            setReceiptLoadingId(null)
        }
    }, [])

    const saveReceipt = useCallback(async (entryId: number) => {
        setReceiptLoadingId(entryId)
        try {
            await downloadRefundReceiptPdf(entryId)
        } catch {
            toast.error('Não foi possível gerar o recibo deste estorno.')
        } finally {
            setReceiptLoadingId(null)
        }
    }, [])

    const closeReceipt = useCallback(() => {
        setReceipt(current => {
            if (current) URL.revokeObjectURL(current.url)
            return null
        })
    }, [])

    const load = useCallback(async (k: HistoryKind, p: number, term?: string) => {
        setLoading(true)
        try {
            const res = await fetchHistory(k, p, term)
            if (res) {
                setEntries(res.entries)
                setTotal(res.total)
            }
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load(kind, page, search)
    }, [kind, page, search, load])

    // Termo novo, primeira página: a página em que ele estava não vale para o recorte.
    useEffect(() => {
        setPage(1)
    }, [search])

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 flex-wrap">
                {KIND_TABS.map(tab => {
                    const Icon = tab.icon
                    const isActive = tab.key === kind
                    return (
                        <button
                            key={tab.key}
                            className={`btn btn-sm gap-2 ${
                                isActive ? 'btn-secondary' : 'btn-ghost'
                            }`}
                            onClick={() => {
                                setKind(tab.key)
                                setPage(1)
                            }}
                        >
                            <Icon className="text-base" />
                            {tab.label}
                        </button>
                    )
                })}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 rounded-full bg-secondary/10 text-secondary flex items-center justify-center mb-3">
                        <MdHistory className="text-3xl" />
                    </div>
                    <p className="font-medium">
                        {search
                            ? 'Nada encontrado para esta busca nesta fila.'
                            : 'Nada aprovado ainda nesta fila.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="flex flex-col gap-2.5">
                        {entries.map(entry => (
                            <div
                                key={`${kind}-${entry.id}`}
                                className="rounded-xl border border-base-300/70 bg-base-100 p-4 flex flex-col gap-2"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            {entry.tag && (
                                                <span className="badge badge-sm bg-secondary/15 text-secondary border-0">
                                                    {entry.tag}
                                                </span>
                                            )}
                                            <span className="text-xs text-base-content/50">
                                                {formatDateTime(entry.approvedAt)}
                                            </span>
                                        </div>
                                        {/* O título leva ao lançamento: o histórico é
                                            onde se investiga "o que foi isso?", e antes
                                            era um beco sem saída. */}
                                        {entry.billingId ? (
                                            <button
                                                className="link link-hover font-medium text-left break-words hover:text-secondary"
                                                onClick={() =>
                                                    navigate(
                                                        `/financial/billings/${entry.billingId}`,
                                                    )
                                                }
                                            >
                                                {entry.title}
                                            </button>
                                        ) : (
                                            <div className="font-medium break-words">
                                                {entry.title}
                                            </div>
                                        )}
                                        <div className="text-sm text-base-content/60 mt-0.5 flex items-center gap-1.5 flex-wrap">
                                            {entry.counterparty}
                                            <span>·</span>
                                            <span className="inline-flex items-center gap-1">
                                                <MdPerson className="text-sm" />
                                                {entry.approvedBy}
                                            </span>
                                            <CompanyBadge company={entry.company} />
                                        </div>
                                    </div>
                                    <div className="font-mono font-semibold whitespace-nowrap shrink-0">
                                        {formatCurrency(entry.amount)}
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-1 flex-wrap">
                                    {entry.hasRefundReceipt && (
                                        <>
                                            <button
                                                className="btn btn-xs btn-ghost gap-1 text-secondary"
                                                disabled={
                                                    receiptLoadingId === entry.id
                                                }
                                                onClick={() =>
                                                    openReceipt(entry.id)
                                                }
                                            >
                                                {receiptLoadingId === entry.id ? (
                                                    <span className="loading loading-spinner loading-xs" />
                                                ) : (
                                                    <MdPrint />
                                                )}
                                                Recibo
                                            </button>
                                            <button
                                                className="btn btn-xs btn-ghost gap-1"
                                                disabled={
                                                    receiptLoadingId === entry.id
                                                }
                                                onClick={() =>
                                                    saveReceipt(entry.id)
                                                }
                                                title="Baixar o recibo em PDF"
                                            >
                                                <MdDownload />
                                                PDF
                                            </button>
                                        </>
                                    )}
                                    {entry.isPayoutBatch && entry.billingId && (
                                        <button
                                            className="btn btn-xs btn-ghost gap-1 text-secondary"
                                            onClick={() =>
                                                setBatchId(entry.billingId!)
                                            }
                                        >
                                            <MdListAlt />
                                            Ver grupo
                                        </button>
                                    )}
                                    {entry.statement && (
                                        <button
                                            className="btn btn-xs btn-ghost gap-1 text-secondary"
                                            onClick={() =>
                                                setStatement(entry.statement!)
                                            }
                                        >
                                            <MdPictureAsPdf />
                                            Demonstrativo
                                        </button>
                                    )}
                                    {entry.billingId && (
                                        <button
                                            className="btn btn-xs btn-ghost gap-1"
                                            onClick={() =>
                                                navigate(
                                                    `/financial/billings/${entry.billingId}`,
                                                )
                                            }
                                        >
                                            <MdOpenInNew />
                                            Abrir lançamento
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > PAGE_SIZE && (
                        <PaginationControls
                            page={page}
                            limit={PAGE_SIZE}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="registro"
                        />
                    )}
                </>
            )}

            {batchId && (
                <PayoutBatchItemsModal
                    batchId={batchId}
                    onClose={() => setBatchId(null)}
                />
            )}

            {statement && (
                <ReceiptPreviewModal
                    isOpen
                    onClose={() => setStatement(null)}
                    url={statement.url}
                    name={statement.name}
                />
            )}

            {receipt && (
                <ReceiptPreviewModal
                    isOpen
                    onClose={closeReceipt}
                    url={receipt.url}
                    name={receipt.name}
                    forceType="pdf"
                />
            )}
        </div>
    )
}

/** Total de decisões já tomadas — alimenta o badge da aba "Aprovados". */
export async function countApprovedHistory(): Promise<number> {
    const [actions, payables, refunds] = await Promise.all([
        getApprovedBillingActionRequests(1, 1),
        getApprovedBillingApprovals(1, 1),
        getApprovedRefundRequests(1, 1),
    ])
    return (
        (actions.success ? (actions.result?.total ?? 0) : 0) +
        (payables.success ? (payables.result?.total ?? 0) : 0) +
        (refunds.success ? (refunds.result?.total ?? 0) : 0)
    )
}
